import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Spinner, Alert, Button, Image, Badge, Modal, Form, Carousel, Toast, ToastContainer, InputGroup, Dropdown, Tabs, Tab, ButtonGroup, Pagination, Offcanvas } from 'react-bootstrap';
import axios from 'axios';
import logoImage from '../../img/logo.png'; // Logo padrão como fallback
import '../../styles/social/LandingPage.css'; // Reutilizar estilos se aplicável
// Importando ícones
import { HandThumbsUp, Chat, Share, Flag, Eye, Search, HouseDoor, Shop, StarFill, Globe, Instagram, Facebook, Whatsapp, ArrowDownUp } from 'react-bootstrap-icons';
import { useAuthContext } from '../../context/AuthContext'; // Importar o contexto de autenticação
import ServicoCard from '../../components/servicos/ServicoCard'; // Importar o novo componente
import CommentComponent from '../../components/social/Comment'; // Importar o novo componente de comentário

// Função para verificar se a data é hoje
const isPublishedToday = (dateString) => {
    if (!dateString) return false;
    const date = new Date(dateString);
    const now = new Date();

    // Trabalhar com datas em UTC para consistência
    const todayUTC = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    const inputDayUTC = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
    
    return inputDayUTC.getTime() === todayUTC.getTime();
};

// Função para formatar a data de publicação de forma relativa
const formatRelativeDate = (dateString) => {
    if (!dateString) return '';

    const date = new Date(dateString);
    const now = new Date();

    // Trabalhar com datas em UTC para consistência
    const todayUTC = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    const yesterdayUTC = new Date(todayUTC);
    yesterdayUTC.setUTCDate(yesterdayUTC.getUTCDate() - 1);
    
    const inputDayUTC = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));

    let datePart;
    if (inputDayUTC.getTime() === todayUTC.getTime()) {
        datePart = 'Hoje';
    } else if (inputDayUTC.getTime() === yesterdayUTC.getTime()) {
        datePart = 'Ontem';
    } else {
        const day = String(date.getUTCDate()).padStart(2, '0');
        const month = String(date.getUTCMonth() + 1).padStart(2, '0'); // Mês é 0-indexado
        datePart = `${day}/${month}`;
    }

    // Usar getUTCHours e getUTCMinutes para exibir a hora correta
    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');

    return `${datePart}, ${hours}:${minutes}`;
};

const PublicServicosPage = () => {
    const { entidadeSlug } = useParams();
    const [entidade, setEntidade] = useState(null);
    const [servicos, setServicos] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [categorias, setCategorias] = useState([]);
    const [selectedCategoria, setSelectedCategoria] = useState(null); // null para "Todos"
    const [sortBy, setSortBy] = useState('recentes'); // 'recentes', 'preco_asc', 'preco_desc'

    // Estados para paginação
    const [currentPage, setCurrentPage] = useState(1);
    const servicosPerPage = 8; // Exibir 8 serviços por página
    const [error, setError] = useState(null);

    // Ref para garantir que a contagem de visualização ocorra apenas uma vez por carregamento da página
    const viewCountedRef = useRef(false);

    // Estados para interações
    const [showCommentsOffcanvas, setShowCommentsOffcanvas] = useState(false);
    const [currentServicoForComment, setCurrentServicoForComment] = useState(null);
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState('');
    const [commentFiles, setCommentFiles] = useState([]); // Novo estado para os arquivos do comentário

    // Estado para a galeria de imagens no Offcanvas
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);

    // Estados para o modal de detalhes do serviço
    const [showServicoDetailModal, setShowServicoDetailModal] = useState(false);
    const [selectedServicoForDetail, setSelectedServicoForDetail] = useState(null);

    // Estado para o botão de seguir
    const [isFollowing, setIsFollowing] = useState(false);

    // Estado para a contagem de seguidores
    const [followersCount, setFollowersCount] = useState(0);

    // Estados para o Toast de notificação
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastVariant, setToastVariant] = useState('success');

    // Estado para o campo de pesquisa
    const [searchTerm, setSearchTerm] = useState('');

    // Obter dados de autenticação do contexto
    const { isLoggedIn, loggedInUser, handleLogout } = useAuthContext();

     // Estado para controlar a aba ativa (Serviços, Publicações, etc.)
    const [activeTab, setActiveTab] = useState('servicos');

    // Filtra os serviços com base no termo de pesquisa
    const filteredAndSortedServicos = useMemo(() => {
        let items = [...servicos];

        // 1. Filtrar por categoria
        if (selectedCategoria) {
            items = items.filter(servico => servico.categoria_id === selectedCategoria);
        }

        // 2. Filtrar por termo de busca
        if (searchTerm) {
            const lowercasedFilter = searchTerm.toLowerCase();
            items = items.filter(servico =>
                servico.nome.toLowerCase().includes(lowercasedFilter) ||
                (servico.descricao_publica && servico.descricao_publica.toLowerCase().includes(lowercasedFilter))
            );
        }

        // 3. Ordenar
        switch (sortBy) {
            case 'preco_asc':
                items.sort((a, b) => parseFloat(a.valor) - parseFloat(b.valor));
                break;
            case 'preco_desc':
                items.sort((a, b) => parseFloat(b.valor) - parseFloat(a.valor));
                break;
            case 'recentes':
            default:
                items.sort((a, b) => b.id - a.id); // Assumindo que ID maior é mais recente
                break;
        }

        return items;
    }, [servicos, searchTerm, selectedCategoria, sortBy]);

    // Lógica de Paginação - Parte 1: Cálculo do total de páginas
    const totalPages = Math.ceil(filteredAndSortedServicos.length / servicosPerPage);

    // Gera os itens da paginação com reticências (...)
    const paginationItems = useMemo(() => {
        const delta = 1; // Quantidade de páginas a serem exibidas ao redor da página atual
        const range = [];
        const rangeWithDots = [];
        let l;

        for (let i = 1; i <= totalPages; i++) {
            if (i === 1 || i === totalPages || (i >= currentPage - delta && i <= currentPage + delta)) {
                range.push(i);
            }
        }

        for (let i of range) {
            if (l) {
                if (i - l > 1) rangeWithDots.push('...');
            }
            rangeWithDots.push(i);
            l = i;
        }
        return rangeWithDots;
    }, [totalPages, currentPage]); // A dependência 'totalPages' agora está inicializada

    // Efeito para resetar a página quando os filtros mudam
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, selectedCategoria, sortBy]);
    
    useEffect(() => {
        if (!entidadeSlug) return;

        const fetchPublicData = async () => {
            setIsLoading(true);
            setError(null);
            try {
                // Busca serviços e categorias em paralelo
                const [servicosResponse, categoriasResponse] = await Promise.all([
                    axios.get(`/api/public/entidades/${entidadeSlug}/servicos`),
                    // axios.get(`/api/public/entidades/${entidadeSlug}/categorias`) // Assumindo este endpoint
                ]);

                const entidadeData = servicosResponse.data.entidade;
                setEntidade(entidadeData);

                // Define o estado inicial de "seguir" e a contagem de seguidores
                if (entidadeData) {
                    setIsFollowing(entidadeData.is_followed_by_user || false);
                    setFollowersCount(entidadeData.user_followers_count || 0);
                }

                // setCategorias(categoriasResponse.data.categorias || []);
                
                const fetchedServicos = servicosResponse.data.servicos || [];
                setServicos(fetchedServicos);
            } catch (err) {
                setError(err.response?.data?.message || 'Não foi possível carregar os serviços. A página pode não existir ou estar fora do ar.');
                console.error("Erro ao buscar dados públicos da entidade:", err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchPublicData();
    }, [entidadeSlug]);

    const handleLike = async (servicoId) => {
        if (!isLoggedIn) {
            setToastMessage('Você precisa estar logado para curtir um serviço.');
            setToastVariant('warning');
            setShowToast(true);
            return;
        }

        // Atualização Otimista da UI
        const originalServicos = [...servicos];
        setServicos(prevServicos => prevServicos.map(s => {
            if (s.id === servicoId) {
                const isLiked = !s.is_liked_by_user;
                const likesCount = s.likes_count + (isLiked ? 1 : -1);
                return { ...s, is_liked_by_user: isLiked, likes_count: likesCount };
            }
            return s;
        }));

        try {
            // Chamada à API
            const response = await axios.post(`/api/like/servico/${servicoId}`);

            // Sincroniza o estado com a resposta real do backend (opcional, mas bom para consistência)
            setServicos(prevServicos => prevServicos.map(s => 
                s.id === servicoId ? { ...s, is_liked_by_user: response.data.liked, likes_count: response.data.likes_count } : s
            ));
        } catch (error) {
            console.error("Erro ao curtir o serviço:", error);
            // Reverte a UI em caso de erro
            setServicos(originalServicos);
            setToastMessage('Não foi possível registrar sua curtida. Tente novamente.');
            setToastVariant('danger');
            setShowToast(true);
        }
    };

    const handleShare = (servico) => {
        const shareUrl = window.location.href; // URL da página atual
        const shareTitle = `${servico.nome} - ${entidade.nome}`;
        const shareText = `Confira este serviço: ${servico.nome}`;

        if (navigator.share) {
            navigator.share({
                title: shareTitle,
                text: shareText,
                url: shareUrl,
            }).catch(console.error);
        } else {
            // Fallback para desktop: copiar para a área de transferência
            navigator.clipboard.writeText(shareUrl);
        setToastMessage('Link da página copiado para a área de transferência!');
        setToastVariant('info');
        setShowToast(true);
        }
    };

    const handleOpenComments = async (servico) => {
        if (!servico) return;
        setCurrentServicoForComment(servico);
        setSelectedImageIndex(0); // Reseta para a primeira imagem ao abrir
        setShowCommentsOffcanvas(true);
        try {
            // Busca os comentários da API
            const response = await axios.get(`/api/comments/servico/${servico.id}`);
            setComments(response.data.data || []); // A API paginada retorna os dados em 'data'
        } catch (error) {
            console.error("Erro ao buscar comentários:", error);
            setToastMessage('Não foi possível carregar os comentários.');
            setToastVariant('danger');
            setShowToast(true);
        }
    };

    const handleReport = (servicoId) => {
        // TODO: Implementar um modal de denúncia mais robusto.
        setToastMessage(`Serviço ${servicoId} denunciado. Nossa equipe irá analisar.`);
        setToastVariant('warning');
        setShowToast(true);
    };

    // Handler para mudança de página
    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 400, behavior: 'smooth' }); // Rola suavemente para a seção de serviços
    };

    const handlePostComment = async (text, parentId = null) => {
        // Permite postar apenas com imagem, sem texto
        if ((!text || !text.trim()) && commentFiles.length === 0) return;
        if (!currentServicoForComment) return;

        if (!isLoggedIn) {
            setToastMessage('Você precisa estar logado para comentar.');
            setToastVariant('warning');
            setShowToast(true);
            return;
        }

        const formData = new FormData();
        formData.append('body', text || ''); // Envia string vazia se o texto for nulo
        if (parentId) {
            formData.append('parent_id', parentId);
        }
        commentFiles.forEach(file => {
            formData.append('media[]', file);
        });

        try {
            const response = await axios.post(`/api/comments/servico/${currentServicoForComment.id}`, formData);
    
            const newCommentData = response.data;
    
            if (parentId) {
                // É uma resposta, precisa encontrar o comentário pai e adicionar a resposta a ele
                const addReplyToComment = (comments, parentId, newReply) => {
                    return comments.map(comment => {
                        if (comment.id === parentId) {
                            // Adiciona a nova resposta ao pai
                            return { ...comment, replies: [...comment.replies, newReply] };
                        }
                        if (comment.replies && comment.replies.length > 0) {
                            // Procura recursivamente nas respostas
                            return { ...comment, replies: addReplyToComment(comment.replies, parentId, newReply) };
                        }
                        return comment;
                    });
                };
                setComments(prevComments => addReplyToComment(prevComments, parentId, newCommentData));
            } else {
                // É um comentário principal, adiciona no início da lista
                setComments(prevComments => [newCommentData, ...prevComments]);
                // ATUALIZA A CONTAGEM DE COMENTÁRIOS NO CARD DO SERVIÇO
                setServicos(prevServicos => prevServicos.map(s => 
                    s.id === currentServicoForComment.id 
                        ? { ...s, comments_count: (s.comments_count || 0) + 1 } 
                        : s
                ));
                setNewComment(''); // Limpa o campo de texto
                setCommentFiles([]); // Limpa os arquivos selecionados
            }
        } catch (error) {
            console.error("Erro ao postar comentário:", error);
            setToastMessage('Não foi possível enviar seu comentário.');
            setToastVariant('danger');
            setShowToast(true);
        }
    };

    const handleOpenServicoDetailModal = (servico) => {
        setSelectedServicoForDetail(servico);
        setShowServicoDetailModal(true);
    };

    const handleFollow = async () => {
        if (!isLoggedIn) {
            setToastMessage('Você precisa estar logado para seguir uma clínica.');
            setToastVariant('warning');
            setShowToast(true);
            return;
        }

        // Adiciona uma verificação para garantir que o ID da entidade existe
        if (!entidade || typeof entidade.id === 'undefined') {
            console.error("Ação de seguir falhou: ID da entidade não está disponível.", entidade);
            setToastMessage('Ocorreu um erro ao processar a ação. Tente recarregar a página.');
            setToastVariant('danger');
            setShowToast(true);
            return;
        }

        const originalFollowingState = isFollowing;
        // Atualização otimista da UI para feedback instantâneo
        setIsFollowing(!originalFollowingState);
        setFollowersCount(prevCount => originalFollowingState ? prevCount - 1 : prevCount + 1);

        try {
            // A API usa o tipo 'clinica' e o ID da entidade
            const response = await axios.post(`/api/follow/clinica/${entidade.id}`);
            
            // Sincroniza o estado com a resposta real do backend
            setIsFollowing(response.data.following);
            setFollowersCount(response.data.followers_count);

            setToastMessage(response.data.message);
            setToastVariant(response.data.following ? 'success' : 'info');
            setShowToast(true);
        } catch (error) {
            console.error("Erro ao seguir/deixar de seguir a clínica:", error);
            // Reverte a UI em caso de erro
            setIsFollowing(originalFollowingState);
            setFollowersCount(prevCount => originalFollowingState ? prevCount + 1 : prevCount - 1);
            setToastMessage('Não foi possível completar a ação. Tente novamente.');
            setToastVariant('danger');
            setShowToast(true);
        }
    };

    // Estilo do cabeçalho com imagem de fundo
    const headerStyle = {
        height: '190px', // Altura fixa para a imagem de fundo
        position: 'relative', // Essencial para o posicionamento do overlay
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundImage: `url(${entidade?.imagem_fundo_url || 'https://www.lawrencerealestateonline.com/wp-content/uploads/2018/01/cropped-Chicargo-Skyline-1600-x-400-lq.jpg'})`,
        zIndex: 1,
    };

    // Lógica de Paginação - Parte 2: Seleção dos itens da página atual
    const indexOfLastServico = currentPage * servicosPerPage;
    const indexOfFirstServico = indexOfLastServico - servicosPerPage;
    const currentServicos = filteredAndSortedServicos.slice(indexOfFirstServico, indexOfLastServico);

    if (isLoading) {
        return (
            <Container className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Carregando...</span>
                </Spinner>
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="text-center mt-5">
                <Alert variant="danger">{error}</Alert>
                <Link to="/inicio">Voltar para a página inicial</Link>
            </Container>
        );
    }

    return (
        <div className="landing-page bg-light">
            {/* Barra de Branding FluxoClínica */}
            <div className="bg-white py-2 shadow-sm sticky-top">
                <Container>
                    <Row className="align-items-center">
                        {/* Coluna da Esquerda: Logo e Pesquisa */}
                        <Col md={5} className="d-flex align-items-center">
                            <Link to="/inicio">
                                <Image src={logoImage} alt="FluxoClínica" style={{ height: '50px', marginRight: '20px' }} />
                            </Link>
                            <InputGroup>
                                <Form.Control
                                    type="text"
                                    placeholder="Pesquisar clínicas ou serviços..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                <Button variant="outline-secondary" id="button-search">
                                    <Search />
                                </Button>
                            </InputGroup>
                        </Col>

                        {/* Coluna Central: Navegação Principal */}
                        <Col md={2} className="text-center d-none d-md-block">
                            <Link to="/inicio" className="btn btn-link text-secondary text-decoration-none me-2" title="Página Inicial">
                                <HouseDoor size={24} />
                            </Link>
                            <Link to="/clinicas" className="btn btn-link text-secondary text-decoration-none" title="Ver todas as Clínicas">
                                <Shop size={24} />
                            </Link>
                        </Col>

                        {/* Coluna da Direita: Ações do Usuário (espaço reservado) */}
                        <Col md={5} className="text-end">
                            {isLoggedIn ? (
                                <Dropdown align="end">
                                    <Dropdown.Toggle as="a" style={{ cursor: 'pointer' }} id="dropdown-user-menu">
                                        <Image
                                            src={loggedInUser.foto_path ? `${process.env.REACT_APP_API_URL}/storage/${loggedInUser.foto_path}` : `https://ui-avatars.com/api/?name=${encodeURIComponent(loggedInUser.nome)}&background=0D6EFD&color=fff`}
                                            alt={`Foto de ${loggedInUser.nome}`}
                                            roundedCircle
                                            style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                                        />
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                        <Dropdown.Header>
                                            Logado como<br/>
                                            <strong>{loggedInUser.nome}</strong>
                                        </Dropdown.Header>
                                        <Dropdown.Divider />
                                        <Dropdown.Item as={Link} to="/perfil">Meu Perfil</Dropdown.Item>
                                        <Dropdown.Item as={Link} to="/dashboard">Meu Painel</Dropdown.Item>
                                        <Dropdown.Divider />
                                        <Dropdown.Item onClick={handleLogout}>Sair</Dropdown.Item>
                                    </Dropdown.Menu>
                                </Dropdown>
                            ) : (
                                <>
                                    <Link to="/login" className="btn btn-outline-primary me-2">Login</Link>
                                    <Link to="/register" className="btn btn-primary">Cadastre-se</Link>
                                </>
                            )}
                        </Col>
                    </Row>
                </Container>
            </div>

            {/* Header da Página Pública */}
            <header className="hero-section" style={headerStyle}>
                {/* Overlay para garantir a legibilidade do texto */}
                <div style={{
                    // position: 'absolute',
                    top:    0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(29, 38, 113, 0.7)',
                    zIndex: -1,
                }}></div>
            </header>

            {/* Subheader e Conteúdo Principal */}
            <Container className="entidade-content-container">
                <div className="entidade-logo-floating">
                    <Image src={entidade?.logo_url || logoImage} alt={`Logo de ${entidade?.nome}`} roundedCircle />
                </div>
                <div className="entidade-subheader-card">
                    <Row className="align-items-center">
                        <Col md={7}>
                            <div className="entidade-nome-wrapper">
                                <h1 className="display-6 fw-bold text-start">{entidade?.nome}</h1>
                                <p className="lead my-1 text-start">Conheça nossos serviços e agende sua consulta.</p>
                            </div>
                        </Col>
                        <Col md={5} className="text-end">
                            <div className="mb-2">
                                <span className="fw-bold">{followersCount.toLocaleString('pt-BR')}</span>
                                <span className="ms-2 text-muted">seguidores</span>
                            </div>
                            <Button variant="outline-primary" className="me-2" onClick={() => alert('Funcionalidade de mensagem em breve!')}>
                                Enviar Mensagem
                            </Button>
                            <Button 
                                variant={isFollowing ? 'primary' : 'outline-primary'}
                                onClick={handleFollow}
                                className="fw-bold"
                            >
                                {isFollowing ? 'Seguindo' : 'Seguir'}
                            </Button>
                        </Col>
                    </Row>
                </div>
            </Container>

            {/* Catálogo de Serviços */}
            <Container className="py-5 page-width-container">
                <Row>
                    {/* Coluna da Esquerda: Informações da Clínica */}
                    <Col lg={3} className="mb-4 mb-lg-0">
                        <Card className="shadow-sm sticky-top" style={{ top: '80px' }}>
                            <Card.Body>
                                <Card.Title as="h5" className="mb-3">Sobre a Clínica</Card.Title>
                                <div className="mb-3">
                                    <StarFill className="text-warning me-1" />
                                    <StarFill className="text-warning me-1" />
                                    <StarFill className="text-warning me-1" />
                                    <StarFill className="text-warning me-1" />
                                    <StarFill className="text-warning me-1" />
                                    <span className="ms-2 text-muted">(5.0)</span>
                                </div>
                                <p className="small text-muted">
                                    Membro desde: {entidade?.created_at ? new Date(entidade.created_at).toLocaleDateString('pt-BR') : 'Data indisponível'}
                                </p>
                                <hr />
                                <h6 className="mb-3">Apresentação</h6>
                                <p className="small text-muted">
                                    {entidade?.descricao_institucional || 'Nenhuma descrição fornecida pela clínica.'}
                                </p>
                                <hr />
                                <h6 className="mb-3">Redes Sociais e Contato</h6>
                                <div className="d-flex justify-content-start">
                                    {entidade?.website_url && <a href={entidade.website_url} target="_blank" rel="noopener noreferrer" className="text-secondary me-3" title="Website"><Globe size={24} /></a>}
                                    {entidade?.instagram_url && <a href={entidade.instagram_url} target="_blank" rel="noopener noreferrer" className="text-secondary me-3" title="Instagram"><Instagram size={24} /></a>}
                                    {entidade?.facebook_url && <a href={entidade.facebook_url} target="_blank" rel="noopener noreferrer" className="text-secondary me-3" title="Facebook"><Facebook size={24} /></a>}
                                    {entidade?.whatsapp_number && <a href={`https://wa.me/${entidade.whatsapp_number}`} target="_blank" rel="noopener noreferrer" className="text-secondary" title="WhatsApp"><Whatsapp size={24} /></a>}
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Coluna da Direita: Conteúdo com Abas */}
                    <Col lg={9}>
                        <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} id="public-content-tabs" className="mb-3" fill>
                            <Tab eventKey="servicos" title="Serviços">
                                <div className="pt-2">
                                    {/* Filtros e Ordenação */}
                                    <Card className="mb-4 shadow-sm">
                                        <Card.Body className="p-2">
                                            <Row className="align-items-center">
                                                <Col md={8}>
                                                    <ButtonGroup size="sm" className="flex-wrap">
                                                        <Button variant={!selectedCategoria ? 'primary' : 'outline-primary'} onClick={() => setSelectedCategoria(null)}>Todos</Button>
                                                        {categorias.map(cat => (
                                                            <Button key={cat.id} variant={selectedCategoria === cat.id ? 'primary' : 'outline-primary'} onClick={() => setSelectedCategoria(cat.id)}>{cat.nome}</Button>
                                                        ))}
                                                    </ButtonGroup>
                                                </Col>
                                                <Col md={4} className="text-end mt-2 mt-md-0">
                                                    <Dropdown onSelect={(eventKey) => setSortBy(eventKey)} size="sm">
                                                        <Dropdown.Toggle variant="outline-secondary" id="dropdown-sort">
                                                            <ArrowDownUp className="me-2"/>
                                                            Ordenar por
                                                        </Dropdown.Toggle>
                                                        <Dropdown.Menu>
                                                            <Dropdown.Item eventKey="recentes" active={sortBy === 'recentes'}>Mais Recentes</Dropdown.Item>
                                                            <Dropdown.Item eventKey="preco_asc" active={sortBy === 'preco_asc'}>Preço: Menor para Maior</Dropdown.Item>
                                                            <Dropdown.Item eventKey="preco_desc" active={sortBy === 'preco_desc'}>Preço: Maior para Menor</Dropdown.Item>
                                                        </Dropdown.Menu>
                                                    </Dropdown>
                                                </Col>
                                            </Row>
                                        </Card.Body>
                                    </Card>

                                    <Row xs={1} md={2} lg={3} xl={4} className="g-4">
                                        {servicos.length === 0 ? (
                                            <Col xs={12}><Alert variant="info" className="text-center">Nenhum serviço disponível para visualização no momento.</Alert></Col>
                                        ) : filteredAndSortedServicos.length === 0 ? (
                                            <Col xs={12}><Alert variant="secondary" className="text-center">Nenhum serviço encontrado para os filtros selecionados.</Alert></Col>
                                        ) : (
                                            currentServicos.map(servico => (
                                                <ServicoCard
                                                    key={servico.id}
                                                    servico={servico}
                                                    isPublishedToday={isPublishedToday}
                                                    formatRelativeDate={formatRelativeDate}
                                                    onLike={handleLike}
                                                    onComment={handleOpenComments}
                                                    onShare={handleShare}
                                                    onReport={handleReport}
                                                    onInfoClick={handleOpenServicoDetailModal}
                                                    isLoggedIn={isLoggedIn}
                                                />
                                            ))
                                        )}
                                    </Row>

                                    {/* Componente de Paginação */}
                                    {totalPages > 1 && (
                                        <Row className="mt-4">
                                            <Col className="d-flex justify-content-center">
                                                <Pagination>
                                                    <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
                                                    <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />                                                    
                                                    {paginationItems.map((item, index) => (
                                                        item === '...' ? (
                                                            <Pagination.Ellipsis key={`ellipsis-${index}`} disabled />
                                                        ) : (
                                                            <Pagination.Item
                                                                key={item}
                                                                active={item === currentPage}
                                                                onClick={() => handlePageChange(item)}
                                                            >
                                                                {item}
                                                            </Pagination.Item>
                                                        )
                                                    ))}
                                                    <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                                                    <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
                                                </Pagination>
                                            </Col>
                                        </Row>
                                    )}
                                </div>
                            </Tab>
                            <Tab eventKey="publicacoes" title="Publicações">
                                <div className="py-3"><Alert variant="secondary">Conteúdo sobre publicações em breve.</Alert></div>
                            </Tab>
                            <Tab eventKey="videos" title="Vídeos">
                                <div className="py-3"><Alert variant="secondary">Conteúdo de vídeos em breve.</Alert></div>
                            </Tab>
                        </Tabs>
                    </Col>
                </Row>
            </Container>

            {/* Offcanvas de Comentários (substitui o Modal) */}
            <Offcanvas 
                show={showCommentsOffcanvas} 
                onHide={() => setShowCommentsOffcanvas(false)} 
                placement="end" 
                className="offcanvas-comments"
            >
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title>Comentários sobre "{currentServicoForComment?.nome}"</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    <Row className="h-100">
                        {/* Coluna da Esquerda: Galeria de Imagens */}
                        <Col md={6} className="d-flex flex-column offcanvas-comments-gallery p-3">
                            {/* Imagem Principal */}
                            <div className="main-image-container mb-3">
                                {currentServicoForComment?.imagens && currentServicoForComment.imagens.length > 0 ? (
                                    <Image 
                                        src={currentServicoForComment.imagens[selectedImageIndex]?.url} 
                                        alt={`Imagem de ${currentServicoForComment.nome}`} 
                                        fluid 
                                        rounded 
                                        className="shadow-sm"
                                    />
                                ) : (
                                    <div className="no-image-placeholder">
                                        <p>Sem Imagens</p>
                                    </div>
                                )}
                            </div>

                            {/* Miniaturas (Thumbnails) */}
                            {currentServicoForComment?.imagens && currentServicoForComment.imagens.length > 1 && (
                                <div className="thumbnail-gallery">
                                    {currentServicoForComment.imagens.map((imagem, index) => (
                                        <Image 
                                            key={imagem.id}
                                            src={imagem.url}
                                            alt={`Thumbnail ${index + 1}`}
                                            rounded
                                            className={`thumbnail-image ${index === selectedImageIndex ? 'active' : ''}`}
                                            onClick={() => setSelectedImageIndex(index)}
                                        />
                                    ))}
                                </div>
                            )}
                            <div className="mt-3 flex-grow-1" style={{ overflowY: 'auto' }}>
                                <h5 className="mb-2">Sobre o Serviço</h5>
                                <p className="text-muted small">{currentServicoForComment?.descricao_publica || currentServicoForComment?.descricaoOriginal || 'Nenhuma descrição detalhada fornecida.'}</p>
                            </div>
                        </Col>

                        {/* Coluna da Direita: Comentários */}
                        <Col md={6} className="d-flex flex-column h-100 offcanvas-comments-thread">
                            <div className="flex-grow-1 mb-3" style={{ overflowY: 'auto', paddingLeft: '15px' }}>
                                {comments.length > 0 ? comments.map(comment => (
                                    <CommentComponent key={comment.id} comment={comment} onReplySubmit={handlePostComment} />
                                )) : (
                                    <div className="text-center text-muted p-5 border rounded h-100 d-flex align-items-center justify-content-center">
                                        <p>Nenhum comentário ainda. Seja o primeiro a comentar!</p>
                                    </div>
                                )}
                            </div>
                            <div className="mt-auto" style={{ paddingLeft: '15px' }}> {/* Formulário fica no rodapé */}
                                <Form onSubmit={(e) => { e.preventDefault(); handlePostComment(newComment); }}>
                                    <Form.Group className="mb-2">
                                        <Form.Control as="textarea" rows={3} placeholder="Escreva seu comentário..." value={newComment} onChange={(e) => setNewComment(e.target.value)} required />
                                    </Form.Group>
                                    {/* Preview das imagens a serem enviadas */}
                                    {commentFiles.length > 0 && (
                                        <div className="d-flex flex-wrap gap-2 mb-2">
                                            {Array.from(commentFiles).map((file, index) => (
                                                <Image key={index} src={URL.createObjectURL(file)} alt={`preview ${index}`} style={{ height: '50px', width: '50px', objectFit: 'cover' }} rounded />
                                            ))}
                                        </div>
                                    )}
                                    <div className="d-flex justify-content-between align-items-center mt-2">
                                        <Form.Group controlId="commentFile">
                                            <Form.Control type="file" multiple onChange={(e) => setCommentFiles(e.target.files)} size="sm" />
                                        </Form.Group>
                                        <Button variant="primary" type="submit" disabled={!isLoggedIn}>
                                            {isLoggedIn ? 'Enviar Comentário' : 'Faça login para comentar'}
                                        </Button>
                                    </div>
                                </Form>
                            </div>
                        </Col>
                    </Row>
                </Offcanvas.Body>
            </Offcanvas>

            {/* Modal de Detalhes do Serviço */}
            <Modal show={showServicoDetailModal} onHide={() => setShowServicoDetailModal(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>{selectedServicoForDetail?.nome}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedServicoForDetail && (
                        <>
                            <Carousel>
                                {(selectedServicoForDetail.imagens && selectedServicoForDetail.imagens.length > 0) ? selectedServicoForDetail.imagens.map(imagem => (
                                    <Carousel.Item key={imagem.id}>
                                        <Image
                                            src={imagem.url}
                                            alt={`Imagem de ${selectedServicoForDetail.nome}`}
                                            className="d-block w-100"
                                            style={{ maxHeight: '500px', objectFit: 'contain' }}
                                        />
                                    </Carousel.Item>
                                )) : (
                                    <Image src={'https://via.placeholder.com/800x600?text=Sem+Imagem'} className="d-block w-100" />
                                )}
                            </Carousel>
                            <hr />
                            <h5>Descrição</h5>
                            <p>{selectedServicoForDetail.descricao_publica || selectedServicoForDetail.descricaoOriginal}</p>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button as={Link} to="/register" variant="primary">Agendar este Serviço</Button>
                    <Button variant="secondary" onClick={() => setShowServicoDetailModal(false)}>Fechar</Button>
                </Modal.Footer>
            </Modal>

            {/* Toast de Notificação */}
            <ToastContainer
                position="top-end"
                className="p-3"
                style={{ zIndex: 1055 }} // zIndex para ficar acima do modal se necessário
            >
                <Toast 
                    onClose={() => setShowToast(false)} 
                    show={showToast} 
                    delay={4000} 
                    autohide
                    bg={toastVariant}
                    className="text-white"
                >
                    <Toast.Header closeButton={false}>
                        <strong className="me-auto">Notificação</strong>
                    </Toast.Header>
                    <Toast.Body>{toastMessage}</Toast.Body>
                </Toast>
            </ToastContainer>
        </div>
    );
};

export default PublicServicosPage;